// Constants
import { ModalType } from "@ms/uno-constants/lib/local/ModalType";
import { TaskEditorPanelDialogFallbackBreakpoint } from "@ms/uno-constants/lib/local/UIConstants";
import { TaskEditorRenderMode } from "@ms/planner-models/lib/local/types/TaskEditorRenderMode";
import { TaskEditorSidePanelType } from "@ms/planner-models/lib/local/types/TaskEditorSidePanelType";
// Styles
import { TaskEditorPanelMaxWidth, TaskEditorPanelWidthRatio } from "@ms/planner-presentational-controls/lib/local/taskEditorContent/TaskEditorContent.styles";
/** Blurs the focused task editor field so its normal commit handler runs before the editor remounts. */ export function commitActiveTaskEditorEdit() {
    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement && activeElement.closest("[data-commit-on-resize]")) {
        activeElement.blur();
    }
}
/** Task editor panel width for a base region width: a capped fraction of the region. */ export function getTaskEditorPanelWidth(baseWidth) {
    return Math.min(Math.round(baseWidth * TaskEditorPanelWidthRatio), TaskEditorPanelMaxWidth);
}
/** Whether the task editor panel occupies layout space: flight on, Panel mode, wide viewport, and an open task editor modal. */ export function isTaskEditorPanelActive(viewStore, modalStore, isTaskEditorPanelEnabled) {
    if (!isTaskEditorPanelEnabled) {
        return false;
    }
    if (viewStore.getTaskEditorRenderMode() !== TaskEditorRenderMode.Panel) {
        return false;
    }
    // Responsive fallback: at narrow widths the editor renders as a dialog, not a side panel.
    if (window.innerWidth <= TaskEditorPanelDialogFallbackBreakpoint) {
        return false;
    }
    return modalStore.getCurrentModalDataLayers().some((modal)=>modal.modalType === ModalType.TaskEditor);
}
/** The open task editor modal when the panel is active, otherwise undefined (single-guard convenience over isTaskEditorPanelActive). */ export function getActiveTaskEditorPanelModal(viewStore, modalStore, isTaskEditorPanelEnabled) {
    if (!isTaskEditorPanelActive(viewStore, modalStore, isTaskEditorPanelEnabled)) {
        return undefined;
    }
    return modalStore.getCurrentModalDataLayers().find((modal)=>modal.modalType === ModalType.TaskEditor);
}
/** Returns the persisted side-panel choice only when that panel is available. */ export function getEffectiveTaskEditorSidePanel(activeSidePanel, enableTaskChat, enableTaskHistory) {
    if (activeSidePanel === TaskEditorSidePanelType.Chat && !enableTaskChat) {
        return TaskEditorSidePanelType.None;
    }
    if (activeSidePanel === TaskEditorSidePanelType.History && !enableTaskHistory) {
        return TaskEditorSidePanelType.None;
    }
    return activeSidePanel;
}
/** Resolves the effective side panel for the open task from its current data and permissions. */ export function getEffectiveTaskEditorSidePanelForTask(taskId, stores, configProvider) {
    const activeSidePanel = stores.viewStore.getTaskEditorActiveSidePanel();
    const config = configProvider();
    if (activeSidePanel === TaskEditorSidePanelType.Chat) {
        let enableTaskChat = false;
        if (!config.settings.disableTaskChat) {
            const task = stores.taskStore.getTask(taskId).data;
            const plan = task ? stores.planStore.getPlan(task.planId).data : null;
            enableTaskChat = plan != null && stores.permissionsStore.getContainerPermissions(plan.container, true, false).canUseTaskChat;
        }
        return getEffectiveTaskEditorSidePanel(activeSidePanel, enableTaskChat, false);
    }
    if (activeSidePanel === TaskEditorSidePanelType.History) {
        if (!stores.taskStore.getTask(taskId).data) {
            return TaskEditorSidePanelType.None;
        }
        const enableTaskHistory = config.flights.EnableTaskHistory && stores.permissionsStore.getUserPermissions().canViewTaskHistory;
        return getEffectiveTaskEditorSidePanel(activeSidePanel, false, enableTaskHistory);
    }
    return TaskEditorSidePanelType.None;
}
/** Primary view width minus the full task editor sidecar when it is open (getPrimaryViewWidth already excludes left nav + Copilot sidecar). */ export function getPrimaryViewContentWidth(stores, configProvider) {
    const { viewStore, modalStore } = stores;
    const baseWidth = viewStore.getPrimaryViewWidth();
    const activeTaskEditorModal = getActiveTaskEditorPanelModal(viewStore, modalStore, configProvider().flights.EnableTaskEditorPanel);
    if (!activeTaskEditorModal) {
        return baseWidth;
    }
    const sidePanelWidth = getEffectiveTaskEditorSidePanelForTask(activeTaskEditorModal.taskId, stores, configProvider) === TaskEditorSidePanelType.None ? 0 : viewStore.getTaskEditorSidePanelWidth();
    return Math.max(0, baseWidth - getTaskEditorPanelWidth(baseWidth) - sidePanelWidth);
}
